import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronDown, FaChevronRight, FaSignOutAlt } from "react-icons/fa";
import { useTheme } from "../context/SocketContext";
import {
  LuCircleHelp,
  LuUserCog,
  LuCircleUser,
  LuFileText,
  LuHistory,
  LuCreditCard,
  LuVideo,
  LuReceipt,
  LuBookOpenCheck,
  LuBuilding2,
  LuBriefcaseBusiness,
  LuShieldCheck,
  LuCalendarDays,
  LuGift,
  LuFolderKanban,
  LuLock,
  LuLayoutDashboard
} from "react-icons/lu";
import logo from "../Assets/Bookscomply2.png";

const Sidebar = ({ sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed }) => {
  const [accountDropdown, setAccountDropdown] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const { currentThemeColors } = useTheme();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data.");

        const data = await response.json();
        setUser(data.user);
      } catch (error) {
        console.error("User fetch error:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen, setSidebarOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleDropdown = () => {
    setAccountDropdown((prev) => !prev);
  };

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = () => {
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  const NavLink = ({ to, icon: Icon, children, disabled = false, index = 0 }) => {
    if (disabled) {
      return (
        <div 
          className="sidebar-link-disabled opacity-0 animate-fade-in-up" 
          style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
          title={sidebarCollapsed ? children : ''}
        >
          <LuLock className="w-5 h-5 text-gray-400 dark:text-dark-500" />
          {!sidebarCollapsed && <span>{children}</span>}
        </div>
      );
    }

    return (
      <Link
        to={to}
        onClick={handleLinkClick}
        className={`sidebar-link group ${isActive(to) ? 'active' : ''} opacity-0 animate-fade-in-up`}
        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
        title={sidebarCollapsed ? children : ''}
      >
        <Icon 
          className={`w-5 h-5 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${isActive(to) ? 'text-white' : ''}`} 
          style={{ color: isActive(to) ? 'white' : 'var(--primary-500)' }}
        />
        {!sidebarCollapsed && <span className="font-medium">{children}</span>}
        {isActive(to) && !sidebarCollapsed && (
          <div className="absolute right-3 w-2 h-2 bg-white rounded-full animate-ping-slow" />
        )}
      </Link>
    );
  };

  return (
    <aside 
      ref={sidebarRef}
      className={`
        fixed top-0 left-0 z-50 h-screen
        bg-white dark:bg-dark-800
        border-r-2 border-gray-200 dark:border-dark-700
        transform transition-all duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:w-20 w-72' : 'w-72'}
        flex flex-col
        shadow-xl lg:shadow-none
      `}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700 gap-2">
        <div className={`flex-1 flex items-center ${sidebarCollapsed ? 'justify-start' : 'pr-6'}`}>
          {!sidebarCollapsed && (
            <img
              src={logo}
              alt="BooksComply"
              className="h-10 w-auto max-w-[250px] object-contain mr-10"
            />
          )}
        </div>
        <button 
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
        >
          <FaChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
        <button 
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:block p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <FaChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          ) : (
            <FaChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          )}
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto py-4 px-3 scrollbar-hide">
        {/* Main Navigation */}
        <nav className="space-y-1">
          <NavLink to="/dashboard" icon={LuLayoutDashboard} index={0}>Dashboard</NavLink>
          <NavLink to="/help" icon={LuCircleHelp} index={1}>Help Guide</NavLink>

          {/* Account Settings Dropdown */}
          {!sidebarCollapsed ? (
            <div className="relative">
              <button
                onClick={toggleDropdown}
                className={`
                  w-full sidebar-link justify-between
                  ${accountDropdown ? 'bg-primary-50 dark:bg-dark-700 text-primary-600 dark:text-primary-400' : ''}
                `}
              >
                <div className="flex items-center gap-3">
                  <LuUserCog className={`w-5 h-5 ${accountDropdown ? 'text-primary-500' : 'text-primary-500 dark:text-primary-400'}`} />
                  <span className="font-medium">Account Settings</span>
                </div>
                <FaChevronDown className={`w-4 h-4 transition-transform duration-200 ${accountDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <div className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${accountDropdown ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}
              `}>
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-primary-200 dark:border-dark-600 pl-4">
                  <Link
                    to="/profile"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/profile') 
                        ? 'bg-primary-500 text-white shadow-md' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
                      }`}
                  >
                    <LuCircleUser className="w-4 h-4" />
                    My Profile
                  </Link>
                  <Link
                    to="/invoice"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/invoice') 
                        ? 'bg-primary-500 text-white shadow-md' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
                      }`}
                  >
                    <LuFileText className="w-4 h-4" />
                    Invoice Download
                  </Link>
                  <Link
                    to="/payment-history"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/payment-history') 
                        ? 'bg-primary-500 text-white shadow-md' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
                      }`}
                  >
                    <LuHistory className="w-4 h-4" />
                    Payment History
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              <NavLink to="/profile" icon={LuCircleUser} index={1}>My Profile</NavLink>
              <NavLink to="/invoice" icon={LuFileText} index={2}>Invoice Download</NavLink>
              <NavLink to="/payment-history" icon={LuHistory} index={3}>Payment History</NavLink>
            </>
          )}

          <NavLink to="/documents" icon={LuFolderKanban} index={2}>Documents</NavLink>
          <NavLink to="/pricing" icon={LuCreditCard} index={3}>Pricing Plan</NavLink>
          <NavLink to="/meetings" icon={LuVideo} index={4}>Meetings</NavLink>
        </nav>

        {/* Services Section */}
        <div className="mt-6">
          {!sidebarCollapsed && (
            <>
              <div className="px-4 mb-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400">
                  Services
                </h3>
              </div>
              <div className="h-px bg-gradient-to-r from-primary-500/50 via-secondary-500/50 to-transparent mb-3" />
            </>
          )}
          
          <nav className="space-y-1">
            <NavLink to="/tax-filing" icon={LuReceipt} disabled={!user?.isTaxFilingEnabled} index={5}>
              Tax Filing
            </NavLink>
            <NavLink to="/bookkeeping" icon={LuBookOpenCheck} disabled={!user?.isBookkeepingEnabled} index={6}>
              Bookkeeping
            </NavLink>
            <NavLink to="/usa" icon={LuBuilding2} disabled={!user?.isUSAEnabled} index={7}>
              USA Company
            </NavLink>
            <NavLink to="/cfo" icon={LuBriefcaseBusiness} disabled={!user?.isCFOEnabled} index={8}>
              CFO Advisory
            </NavLink>
            <NavLink to="/registered-agent" icon={LuShieldCheck} disabled={!user?.isRegisteredAgentEnabled} index={9}>
              Registered Agent
            </NavLink>
            <NavLink to="/annual-report" icon={LuCalendarDays} disabled={!user?.isAnnualReportServiceEnabled} index={10}>
              Annual Report Service
            </NavLink>
          </nav>
        </div>
      </div>

      {/* Refer & Earn - Highlighted Special Button */}
      <div className="px-3 pb-2 border-t border-gray-100 dark:border-dark-700 pt-3">
        <Link
          to="/refer-and-earn"
          onClick={handleLinkClick}
          className={`group relative flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-4 py-3.5 rounded-xl overflow-hidden transition-all duration-300 ${
            isActive('/refer-and-earn') 
              ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/40' 
              : 'bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-pink-500/10 dark:from-amber-500/20 dark:via-orange-500/20 dark:to-pink-500/20 text-amber-700 dark:text-amber-400 hover:from-amber-500 hover:via-orange-500 hover:to-pink-500 hover:text-white hover:shadow-lg hover:shadow-orange-500/30'
          }`}
          title={sidebarCollapsed ? 'Refer & Earn - Get $5 credit!' : ''}
        >
          {/* Animated Background Shimmer */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
          
          {/* Gift Icon with Animation */}
          <div className={`relative p-2 rounded-lg transition-all duration-300 ${
            isActive('/refer-and-earn') 
              ? 'bg-white/20' 
              : 'bg-gradient-to-br from-amber-500 to-orange-500 group-hover:bg-white/20'
          }`}>
            <LuGift className={`w-5 h-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${
              isActive('/refer-and-earn') ? 'text-white' : 'text-white group-hover:text-white'
            }`} />
          </div>
          
          {/* Text Content */}
          {!sidebarCollapsed && (
            <>
              <div className="flex flex-col">
                <span className="font-bold text-sm">Refer & Earn</span>
                <span className={`text-xs transition-colors ${
                  isActive('/refer-and-earn') 
                    ? 'text-white/80' 
                    : 'text-amber-600/70 dark:text-amber-400/70 group-hover:text-white/80'
                }`}>
                  Get 1000 Points!
                </span>
              </div>
              
              {/* Arrow Icon */}
              <FaChevronRight className={`w-4 h-4 ml-auto transition-transform duration-300 group-hover:translate-x-1 ${
                isActive('/refer-and-earn') ? 'text-white' : 'text-amber-500 group-hover:text-white'
              }`} />
            </>
          )}
          
          {/* Active Indicator */}
          {isActive('/refer-and-earn') && !sidebarCollapsed && (
            <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full animate-pulse" />
          )}
        </Link>
      </div>

      {/* Logout Button - Mobile Only */}
      <div className="p-4 border-t border-gray-100 dark:border-dark-700 lg:hidden">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl
            bg-gradient-to-r from-red-500 to-red-600
            text-white font-semibold
            shadow-lg shadow-red-500/30
            hover:shadow-xl hover:shadow-red-500/40
            hover:from-red-600 hover:to-red-700
            active:scale-[0.98]
            transition-all duration-200"
        >
          <FaSignOutAlt className="w-5 h-5" />
          Logout
        </button>
      </div>

      {/* 3D Decorative Element */}
      <div 
        className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl pointer-events-none animate-pulse-slow" 
        style={{ background: `linear-gradient(135deg, ${currentThemeColors?.primary}20, ${currentThemeColors?.accent}20)` }}
      />
    </aside>
  );
};

export default Sidebar;